import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { Button, Input, Modal, Space, Typography, message } from "antd";
import { StarFilled } from "@ant-design/icons";
import axios from "axios";
import "./RequestPremium.css";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

function buildFallbackAxios() {
  const inst = axios.create({
    baseURL: "/api",
    headers: { "Content-Type": "application/json" },
  });
  inst.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) config.headers["Authorization"] = `Bearer ${token}`;
      return config;
    },
    (error) => Promise.reject(error)
  );
  return inst;
}

/**
 * RequestPremium button + modal.
 *
 * - Opens a small modal explaining why Premium is needed.
 * - Lets the user send a prefilled message to Sam via the existing Feedback endpoint.
 * - Exposes an imperative `open()` method for "out of credits" flows.
 */
const RequestPremium = forwardRef(function RequestPremium(
  {
    axiosInstance,
    hideButton = false,
    buttonText = "Upgrade to Premium (Unlimited)",
    buttonProps,
    modalTitle = "Premium needed for this feature",
    prefillText,
    context,
  },
  ref
) {
  const api = axiosInstance || buildFallbackAxios();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const defaultPrefill = useMemo(() => {
    const base =
      "Hi Sam - I'd like to get Premium. Can you show me how to upgrade?\n\n" +
      "I understand some features require paid external API keys. Happy to support your work.\n";
    const ctx = String(context || "").trim();
    return ctx ? `${base}\nContext: ${ctx}\n` : base;
  }, [context]);

  const doOpen = useCallback(
    ({ context: nextContext, prefillText: nextPrefill } = {}) => {
      try {
        const next = typeof nextPrefill === "string" && nextPrefill.trim() ? nextPrefill : prefillText || defaultPrefill;
        const ctx = typeof nextContext === "string" ? nextContext.trim() : "";
        setText(ctx ? `${next}\nContext: ${ctx}\n` : next);
      } catch {
        setText(prefillText || defaultPrefill);
      }
      setOpen(true);
    },
    [defaultPrefill, prefillText]
  );

  useImperativeHandle(ref, () => ({ open: doOpen }), [doOpen]);

  const send = useCallback(async () => {
    const msgTxt = String(text || "").trim();
    if (!msgTxt) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("feedback", `[PREMIUM REQUEST]\n${msgTxt}`);
      await api.post("/send_feedback/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      message.success("Sent. Sam will get back to you.");
      setOpen(false);
    } catch {
      message.error("Couldn’t send right now. Please try again.");
    } finally {
      setSending(false);
    }
  }, [api, text]);

  return (
    <>
      {!hideButton ? (
        <Button
          {...(buttonProps || {})}
          size={buttonProps?.size || "small"}
          icon={
            buttonProps?.icon ?? <StarFilled className="RequestPremium__starIcon" />
          }
          className={["RequestPremium__badgeBtn", buttonProps?.className].filter(Boolean).join(" ")}
          onClick={(e) => {
            try {
              buttonProps?.onClick?.(e);
            } catch {
              // ignore
            }
            doOpen();
          }}
        >
          {buttonText}
        </Button>
      ) : null}

      <Modal
        open={open}
        title={modalTitle}
        onCancel={() => setOpen(false)}
        okText="Send"
        cancelText="Cancel"
        okButtonProps={{ loading: sending, disabled: sending || !String(text || "").trim() }}
        onOk={send}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          <Paragraph style={{ marginBottom: 0 }}>
            The app is meant to be free, but some features require <Text strong>paid external API calls</Text>.
            <br />
            Premium helps cover those costs and supports my work.
          </Paragraph>
          <Text type="secondary">Message to Sam (you can edit it):</Text>
          <TextArea rows={5} value={text} onChange={(e) => setText(e.target.value)} />
        </Space>
      </Modal>
    </>
  );
});

export default RequestPremium;


